import { DocumentStorageConfig } from 'framework/documentStorage';
import { FormInputType } from 'domain/shared/core/FormBuilder';
import { mapTabularRows } from 'domain/shared/documentStorage/shared/DocumentTemplatePreviewSection';
import type { RenderPayload } from 'domain/shared/documentStorage/shared/DocumentTemplatePreviewSection';
import { POLISH_OFFICE_PREVIEW_LOCKED_FIELD_IDS } from '../constants';

type TemplateField = DocumentStorageConfig['fields'][number];

const toFormValue = (field: TemplateField) =>
  field.type === FormInputType.CHECKBOX ? field.defaultValue === 'true' : field.defaultValue;

export const mapTemplateFieldsForForm = (fields: TemplateField[]) =>
  fields.map(field => ({
    ...field,
    id: field.isTabular ? `form.tabular.${field.id}` : `form.${field.id}`,
  }));

export const buildFormValues = (fields: TemplateField[]) => {
  const { formValues, tabularValues } = fields.reduce(
    (acc, field) => {
      if (field.isTabular) {
        acc.tabularValues[field.id] = field.defaultTabularValues;
      } else {
        acc.formValues[field.id] = toFormValue(field);
      }
      return acc;
    },
    {
      formValues: {} as Record<string, unknown>,
      tabularValues: {} as Record<string, string[][] | undefined>,
    },
  );

  return {
    ...formValues,
    tabular: tabularValues,
  };
};

export const buildRenderPayload = (fields: TemplateField[]): RenderPayload =>
  fields.reduce(
    (acc, field) => {
      if (field.isTabular) {
        acc.tabularData.push({
          name: field.id,
          values: mapTabularRows(field.defaultTabularValues || []),
        });
      } else {
        acc.fieldsData.push({
          name: field.id,
          value: String(toFormValue(field) ?? ''),
        });
      }
      return acc;
    },
    {
      fieldsData: [],
      tabularData: [],
    } as RenderPayload,
  );

const POLISH_OFFICE_PREVIEW_LOCKED_FIELD_IDS_SET = new Set<string>(POLISH_OFFICE_PREVIEW_LOCKED_FIELD_IDS);

export const disablePolishOfficePreviewLockedFields = (fields: TemplateField[]) =>
  fields.map(field =>
    POLISH_OFFICE_PREVIEW_LOCKED_FIELD_IDS_SET.has(field.id)
      ? {
          ...field,
          disabled: true,
        }
      : field,
  );
